import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { format } from 'date-fns'
import { ArrowUpRight, Mic } from 'lucide-react'
import { AudioWaveform } from '@renderer/overlay/components/audio-waveform'
import { twMerge } from 'tailwind-merge'
import { useAudioNoteStartTimeMs, useAudioNoteStatus } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAudioElapsedTime } from '@renderer/hooks/audio/useAudioElapsedTime'
import { LiveTranscriptSnippet } from '@renderer/overlay/Sidepanel/components/meeting/live-transcript-snippet'
import { OPENS_IN_VIEW_TITLE, SIDEPANEL_CELL_SURFACE } from '@renderer/overlay/Sidepanel/components/cells/surface'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'

interface RecordingMeetingRowProps {
  label: string
  sourceLabel: string
  className?: string
  style?: CSSProperties
  icon?: ReactNode
  startTime?: Date
}

export function RecordingMeetingRow({
  label,
  sourceLabel,
  className,
  style,
  icon,
  startTime,
}: RecordingMeetingRowProps) {
  const { openView } = useViewActions()
  const liveMeeting = useLiveMeetingIdentity()
  const recordingStartTime = useAudioNoteStartTimeMs()
  const status = useAudioNoteStatus()
  const elapsedTime = useAudioElapsedTime()
  const start = startTime ?? recordingStartTime
  const statusLabel =
    status === 'paused'
      ? 'Paused'
      : status === 'reconnecting'
        ? 'Reconnecting'
        : status === 'starting'
          ? 'Starting'
          : status === 'saving'
            ? 'Saving'
            : 'Recording'
  const openLiveMeeting = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel, pointerEvent: event })
  }

  return (
    <button
      type="button"
      data-slot="cell"
      aria-label={`${label}, ${statusLabel.toLowerCase()}`}
      className={twMerge(
        SIDEPANEL_CELL_SURFACE,
        'group/row bg-success-subtle hover:bg-success-subtle-hover text-foreground border-border hover:border-strong flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left',
        className,
      )}
      style={style}
      onClick={openLiveMeeting}
    >
      <span className="text-foreground-secondary flex size-4 shrink-0 items-center justify-center">
        {icon ?? <Mic className="size-3.5" aria-hidden="true" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-1">
            <span className={twMerge('type-ui-sm truncate font-medium', OPENS_IN_VIEW_TITLE)}>{label}</span>
            <ArrowUpRight
              aria-hidden="true"
              className="text-foreground-secondary size-3.5 shrink-0 opacity-0 group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
            />
          </span>
          {start != null && (
            <span className="type-ui-xs shrink-0 tabular-nums">{format(new Date(start), 'h:mmaaa')}</span>
          )}
        </span>
        <span className="text-success-subtle type-ui-xs flex items-center gap-1.5">
          <AudioWaveform className="h-4" />
          {statusLabel}
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">{elapsedTime}</span>
        </span>
        <LiveTranscriptSnippet className="w-full" />
      </span>
    </button>
  )
}
